import { useCallback, useEffect, useState } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import "@aws-amplify/ui-react/styles.css";
import AddBoxIcon from "@mui/icons-material/AddBox";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Box,
  Button,
  Chip,
  Container,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  AddManagerToChurchInput,
  Baptism,
  Church,
  Manager,
  Reported,
  USAddress,
  ValidationError,
} from "baptize-america-client";
import { ListChecks as SeeAttendeesIcon } from "@phosphor-icons/react";
import AddManagerDialog from "../../components/AddManagerDialog";
import { useAlert } from "../../components/AlertProvider";
import ChurchInfoItem from "../../components/ChurchInfoItem";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomAddress from "../../components/CustomAddress";
import { useUserData } from "../../components/DataProvider";
import DebouncedButton from "../../components/DebounceButton";
import TopNavBar from "../../components/TopNavBar";
import UpdateCountDialog from "../../components/UpdateCountDialog";
import {
  addManagerToChurch,
  createRegistration,
  deleteChurch,
  deleteRegistration,
  getChurch,
  getChurchManagers,
  removeManagerFromChurch,
  updateChurch,
} from "../../utils/client";
import {
  chipTime,
  formatAddress,
  humaneReadableTime,
  isAdmin,
  isAttendee,
  isAuthenticated,
  isChurchManager,
  isManagerRole,
} from "../../utils/helper";
import ChurchServiceItem from "../../components/ChurchServiceItem";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";

const styles = {
  titleBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
    mb: 5,
  },
  innerBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 1,
    mb: 1,
  },
  manager: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "row",
    mt: 1,
    mb: 1,
  },
  addressBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  notesBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    mt: 2,
    mb: 2,
  },
  baptismNotes: {
    width: "100%",
    overflow: "nowrap",
    overflowWrap: "break-word",
    flexDirection: "column",
    whiteSpace: "pre-wrap",
    alignItems: "flex-start",
  },
  paper: {
    p: 2,
    mb: 2,
    borderRadius: 4,
    border: 1,
    borderColor: "#CCCCCC",
  },
  infoBox: {
    bgcolor: "rgba(123, 123, 123, 0.2)",
    width: "100%",
    p: 1,
    borderRadius: 2,
  },
  infoText: {
    display: "flex",
    flexDirection: "row",
    fontSize: "14px",
  },
  infoIcon: {
    display: "flex",
    alignSelf: "center",
    mr: 1,
    color: "rgba(123, 123, 123, 1)",
  },
  reportContainer: {
    display: "flex",
    flex: 1,
  },
  stack: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
  },
  singleRow: {
    display: "flex",
    flexDirection: "column",
    width: "50%",
  },
};

const ViewChurch = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const [churchId] = useOutletContext<(string | undefined)[]>();

  const { setAlert } = useAlert();
  const { userRegistrations, setUserRegistrations } = useUserData();
  const navigate = useNavigate();
  const { authStatus, user } = useAuthenticator((context) => [
    context.authStatus,
  ]);
  const { attributes } = useUserData();

  const [churchInfo, setChurchInfo] = useState<Church>();
  const [baptismEvents, setBaptismEvents] = useState<Baptism[]>();
  const [churchManagers, setChurchManagers] = useState<Manager[]>();
  const [removeManager, setRemoveManager] = useState<Manager>();
  const [deleteChurchDialog, setDeleteChurchDialog] = useState(false);
  const [addManagerDialog, setAddManagerDialog] = useState(false);
  const [unregisterDialog, setUnregisterDialog] = useState<Baptism>();
  const [registerDialog, setRegisterDialog] = useState<Baptism>();
  const [updateBaptismCountDialog, setUpdateBaptismCountDialog] =
    useState(false);
  const [updateSalvationCountDialog, setUpdateSalvationCountDialog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isAdminOrManager =
    isAdmin(attributes) || isChurchManager(attributes, churchId);

  const [reported, setReported] = useState<Reported>();

  const handleGetChurchInfo = useCallback(async () => {
    getChurch({ churchId }).then(
      (res) => {
        setIsLoading(false);
        setChurchInfo(res);
        setBaptismEvents(res.baptisms);
        if (isAdminOrManager) {
          setReported(res.reported);
        }
      },
      (fail) => {
        setIsLoading(false);
        setChurchInfo(undefined);
        setBaptismEvents(undefined);
        setReported(undefined);
        console.log(`Failed to get church, ${fail}`);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [churchId, user, isAdminOrManager]);

  useEffect(() => {
    handleGetChurchInfo();
  }, [handleGetChurchInfo]);

  useEffect(() => {
    if (isAdminOrManager) {
      getChurchManagers({ churchId }).then(
        (res) => {
          setChurchManagers(res.managers);
        },
        (fail) => {
          setChurchManagers(undefined);
          console.log(`Failed to get church managers, ${fail}`);
        },
      );
    }
  }, [user, isAdminOrManager, churchId]);

  /* DELETE CHURCH */
  const handleDeleteChurch = async (churchId: string) => {
    deleteChurch({ churchId }).then(
      () => {
        navigate("/churches");
        setAlert({
          showAlert: true,
          alertMsg: "Church Deleted",
          alertType: "success",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error deleting church",
          alertType: "error",
        });
      },
    );
  };

  /* REGISTER + UNREGISTER BAPTISM EVENT */
  const handleRegisterForBaptism = async (event: Baptism) => {
    createRegistration({
      churchId: event.churchId,
      baptismId: event.baptismId,
    }).then(
      () => {
        setUserRegistrations((prevState) => {
          prevState.set(event.baptismId!!, {
            baptism: { baptismId: event.baptismId, time: event.time },
            church: { churchId: churchId, name: churchInfo?.name },
            email: attributes.email,
            firstName: attributes.given_name,
            lastName: attributes.family_name,
            sub: attributes.sub,
          });
          return prevState;
        });
        setAlert({
          showAlert: true,
          alertMsg: `Successfully registered for baptism at ${churchInfo?.name}`,
          alertType: "success",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error registering for baptism",
          alertType: "error",
        });
      },
    );
  };

  const handleUnregisterForBaptism = async (event: Baptism) => {
    const sub = attributes.sub;
    deleteRegistration({
      churchId: event.churchId,
      baptismId: event.baptismId,
      sub,
    }).then(
      () => {
        setUserRegistrations((prevState) => {
          prevState.delete(event.baptismId!);
          return prevState;
        });
        setAlert({
          showAlert: true,
          alertMsg: `Unregistered for baptism at ${churchInfo?.name}`,
          alertType: "warning",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error unregistering for baptism",
          alertType: "error",
        });
      },
    );
  };

  /* ADD + REMOVE CHURCH MANAGERS */
  const handleRemoveManager = async (churchId: string, sub: string) => {
    removeManagerFromChurch({ churchId, sub }).then(
      () => {
        setChurchManagers((curr) => curr?.filter((it) => it.sub !== sub));
        setAlert({
          showAlert: true,
          alertMsg: "Manager removed",
          alertType: "success",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error removing manager",
          alertType: "error",
        });
      },
    );
  };

  const handleAddManager = async (input: AddManagerToChurchInput) => {
    addManagerToChurch(input).then(
      (res) => {
        setChurchManagers((curr) => [
          ...(curr ?? []),
          {
            sub: res.sub,
            firstName: input.firstName,
            lastName: input.lastName,
            email: input.email,
          },
        ]);
        setAlert({
          showAlert: true,
          alertMsg: "Manager added",
          alertType: "success",
        });
      },
      (err) => {
        setAlert({
          showAlert: true,
          alertMsg:
            (err as unknown as ValidationError).errorMessage ??
            "Error adding manager",
          alertType: "error",
        });
      },
    );
  };

  /** UPDATE TOTAL NUMBER OF BAPTISMS + SALVATIONS*/
  const handleUpdateCount = async ({
    updatedCount,
    reportedType,
  }: {
    updatedCount: Reported;
    reportedType: string;
  }) => {
    // validate new values to see if changed using prevChurchInfo:
    try {
      await updateChurch({
        churchId: churchId,
        address: churchInfo?.address,
        instagramUrl: churchInfo?.instagramUrl,
        leadPastor: churchInfo?.leadPastor,
        name: churchInfo?.name,
        websiteUrl: churchInfo?.websiteUrl,
        reported: updatedCount,
      });
      await handleGetChurchInfo();
      setAlert({
        showAlert: true,
        alertMsg: `Total ${reportedType} updated successfully!`,
        alertType: "success",
      });
    } catch (err) {
      console.log(`Unexpected Error updating church - ${JSON.stringify(err)}`);
      setAlert({
        showAlert: true,
        alertMsg:
          (err as unknown as ValidationError).errorMessage ??
          "Error Updating Count",
        alertType: "error",
      });
    }
  };

  const BaptismSalvationReport = () => {
    const ENABLE_REPORTING = true;
    const SHOW_REPORTING = true;

    if (!isAdminOrManager || !SHOW_REPORTING) return null;

    const baptismCountInfo =
      "Please indicate the total number of baptisms that happened at your church (including registered attendees).";
    const salvationCountInfo =
      "How many people who got baptized today also received Jesus in their hearts?";

    return (
      <>
        <Box
          sx={[
            styles.reportContainer,
            { flexDirection: isLargeScreen ? "row" : "column" },
          ]}
        >
          <Box
            sx={[
              styles.paper,
              isLargeScreen ? styles.singleRow : styles.stack,
              isLargeScreen ? { justifyContent: "space-between", mr: 2 } : null,
            ]}
          >
            <Box sx={[styles.innerBox]}>
              <Box sx={{ textAlign: "left" }}>
                <Typography variant={"h6"} sx={{ fontSize: "1.2rem" }}>
                  Total Number of Baptisms
                </Typography>
                <Typography
                  sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
                  component="span"
                >
                  {reported?.baptismPerformed}
                  {ENABLE_REPORTING && (
                    <Chip
                      label="Edit"
                      icon={<EditIcon />}
                      onClick={() => {
                        setUpdateBaptismCountDialog(true);
                      }}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Typography sx={{ fontSize: "1rem", whiteSpace: "pre-line" }}>
                  {baptismCountInfo}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={[styles.paper, isLargeScreen ? styles.singleRow : styles.stack]}
          >
            <Box sx={[styles.innerBox]}>
              <Box sx={{ textAlign: "left" }}>
                <Typography variant={"h6"} sx={{ fontSize: "1.2rem" }}>
                  Total Number of Salvations
                </Typography>
                <Typography
                  sx={{ fontWeight: "bold", fontSize: "1.5rem" }}
                  component="span"
                >
                  {reported?.salvationCount}
                  {ENABLE_REPORTING && (
                    <Chip
                      label="Edit"
                      icon={<EditIcon />}
                      onClick={() => {
                        setUpdateSalvationCountDialog(true);
                      }}
                      sx={{ ml: 1 }}
                    />
                  )}
                </Typography>
                <Typography sx={{ fontSize: "1rem" }}>
                  {salvationCountInfo}
                </Typography>
              </Box>
            </Box>
          </Box>

          <UpdateCountDialog
            open={updateBaptismCountDialog}
            onClose={() => setUpdateBaptismCountDialog(false)}
            onConfirm={handleUpdateCount}
            title={"Number of Baptisms"}
            valueType={"baptismPerformed"}
            currentValue={reported?.baptismPerformed!!}
            previousReported={reported!!}
            helperText={baptismCountInfo}
          />
          <UpdateCountDialog
            open={updateSalvationCountDialog}
            onClose={() => setUpdateSalvationCountDialog(false)}
            onConfirm={handleUpdateCount}
            title={"Number of Salvations"}
            valueType={"salvationCount"}
            currentValue={reported?.salvationCount!!}
            previousReported={reported!!}
            helperText={salvationCountInfo}
          />
        </Box>
        <Box
          sx={[
            styles.paper,
            {
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            },
          ]}
        >
          <Typography variant="h6" sx={{ fontSize: "1.2rem" }}>
            {isLargeScreen ? "Media Submissions" : "Media"}
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant={"outlined"}
              startIcon={<PhotoCameraIcon />}
              href="https://www.dropbox.com/request/wFkBD4tFfTdU1XyhGQ4v"
              target="_blank"
              rel="noopener noreferrer"
            >
              Photos
            </Button>
            <Button
              variant={"outlined"}
              startIcon={<OndemandVideoIcon />}
              href="https://www.dropbox.com/request/awKZHtQPadw0lORasgLu"
              target="_blank"
              rel="noopener noreferrer"
            >
              Videos
            </Button>
          </Box>
        </Box>
      </>
    );
  };

  const EditDeleteChurchButtons = () => {
    if (!isAdminOrManager) {
      return null;
    }
    return (
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button
          variant={"outlined"}
          startIcon={<EditIcon />}
          onClick={() => navigate(`./edit`)}
        >
          {"Edit"}
        </Button>
        {isAdmin(attributes) && (
          <Button
            sx={{ ml: 1, backgroundColor: "#d32f2f", color: "#FFFFFF" }}
            variant={"outlined"}
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setDeleteChurchDialog(true)}
          >
            Delete
          </Button>
        )}
        <ConfirmDialog
          open={deleteChurchDialog}
          onClose={() => setDeleteChurchDialog(false)}
          onConfirm={() => handleDeleteChurch(churchId!!)}
          title={`Delete ${churchInfo?.name}?`}
          content={`Are you sure you want to delete ${churchInfo?.name}? All managers must first be removed, this action cannot be undone.`}
        />
      </div>
    );
  };

  const ChurchDetails = () => {
    const churchInfoText = `This section contains the primary contact and location information for your church.`;

    return churchInfo ? (
      <Box sx={styles.paper}>
        <Box sx={styles.innerBox}>
          <Typography variant={"h6"} sx={{ p: 0 }}>
            {"Details"}
          </Typography>
          <EditDeleteChurchButtons />
        </Box>
        {isAdminOrManager && churchInfoText && (
          <Box sx={styles.infoBox}>
            <Typography align={"left"} sx={styles.infoText}>
              <InfoIcon sx={styles.infoIcon} />
              {churchInfoText}
            </Typography>
          </Box>
        )}
        <ChurchInfoItem label={"Church Name: "} value={churchInfo.name} />
        <Divider orientation="horizontal" />

        <ChurchInfoItem
          label={"Pastor Name: "}
          value={`Pastor ${churchInfo.leadPastor?.firstName} ${churchInfo.leadPastor?.lastName}`}
        />
        <Divider orientation="horizontal" />

        <Box sx={styles.innerBox}>
          <Typography align={"left"} sx={{ color: "#0103077F" }}>
            Address:
          </Typography>
          <CustomAddress
            address={churchInfo?.address as USAddress}
            boxStyle={styles.addressBox}
          />
        </Box>
        <Divider orientation="horizontal" />

        <ChurchInfoItem label={"Website URL: "} value={churchInfo.websiteUrl} />
        <Divider orientation="horizontal" />
        <ChurchInfoItem label={"Instagram: "} value={churchInfo.instagramUrl} />
      </Box>
    ) : (
      <Box sx={[styles.innerBox, { justifyContent: "center", mt: 10 }]}>
        <Typography variant={"h4"}>{"Church Not Found"}</Typography>
      </Box>
    );
  };

  const ChurchBaptisms = () => {
    const baptismInfoText = `List the specific service times during the Baptized America
      weekend (June 7-8, 2025) where baptisms are scheduled to occur at your church.
      Use the notes section to give any special instructions.`;
    const registerButton = {
      fontSize: isAdminOrManager && !isLargeScreen ? "0.8rem" : "1rem",
      mt: 2,
    };
    const buttonContainer = {
      width: !isLargeScreen ? "80%" : "50%",
    };
    const existingRegistration = `Existing registration found. Please unregister before trying again.
        Your existing registration can be found in "Account" tab.`;
    const userRoleRestriction = `Current user (role: Church Manager) may not register for a baptism event.`;

    /** "Register" button with disabled explanation.
     * Disabled when:
     * 1) Church managers: user role attribute starts with "CHURCH"
     * 2) existing Registration (userRegistration.size > 0).
     */
    const RegisterButton = ({
      handlePress,
      isDisabled,
      infoText,
    }: {
      handlePress: () => void;
      isDisabled: boolean;
      infoText: string;
    }) => {
      return (
        <Box sx={buttonContainer}>
          <Tooltip
            enterTouchDelay={20}
            leaveTouchDelay={3000}
            title={infoText}
            sx={{ display: "flex", justifySelf: "center" }}
          >
            <span
              style={{
                width: "100%",
                margin: 0,
                padding: 0,
              }}
            >
              <DebouncedButton
                disabled={isDisabled}
                variant={"outlined"}
                sx={{
                  ...registerButton,
                  backgroundColor: isDisabled ? undefined : "#00169E",
                  color: isDisabled ? undefined : "#FFF",
                }}
                onClick={handlePress}
                fullWidth
              >
                Register
              </DebouncedButton>
            </span>
          </Tooltip>
        </Box>
      );
    };
    return (
      baptismEvents && (
        <Box sx={styles.paper}>
          <Box sx={styles.innerBox}>
            <Typography align={"left"} variant={"h6"}>
              Baptism Services
            </Typography>
            {isAdminOrManager && (
              <Button
                variant={"outlined"}
                startIcon={<AddBoxIcon />}
                onClick={() => navigate(`./baptism/new`)}
              >
                New
              </Button>
            )}
          </Box>
          {isAdminOrManager && baptismInfoText && (
            <Box sx={[styles.infoBox, { mb: 1.5 }]}>
              <Typography align={"left"} sx={styles.infoText}>
                <InfoIcon sx={styles.infoIcon} />
                {baptismInfoText}
              </Typography>
            </Box>
          )}
          {!baptismEvents.length && isAdminOrManager && (
            <Box
              sx={{
                display: "flex",
                fontStyle: "italic",
                mb: 1.5,
                alignItems: "center",
              }}
            >
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Typography
                align={"left"}
                fontWeight={"medium"}
                variant={"h6"}
                color={"warning"}
              >
                {"Please add baptism event info"}
              </Typography>
            </Box>
          )}
          <ChurchServiceItem
            label="Baptism Services:"
            chips={
              baptismEvents?.map((it) => ({
                time: chipTime(it.time!!, it.timezone!!),
                baptismId: it.baptismId!!,
              })) ?? []
            }
          />
          <Typography
            align={"left"}
            fontWeight={"medium"}
            variant="overline"
            color="textSecondary"
          >
            DETAILS
          </Typography>
          {baptismEvents.map((event, index) => (
            <Box
              sx={[styles.paper, { pb: 4 }]}
              key={`${event.baptismId}`}
              id={event.baptismId}
            >
              <div key={event.baptismId}>
                <Box sx={styles.innerBox}>
                  <Typography align={"left"} sx={{ color: "#0103077F" }}>
                    Service Time:
                  </Typography>
                  <Box
                    alignSelf={"right"}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                    }}
                  >
                    <Typography align={"right"}>
                      {`${humaneReadableTime(event?.time!, event?.timezone!)}`}
                    </Typography>
                  </Box>
                </Box>
                <Divider orientation="horizontal" />
                <Box sx={styles.innerBox}>
                  <Typography align={"left"} sx={{ color: "#0103077F" }}>
                    Address:
                  </Typography>
                  <CustomAddress
                    address={event?.address as USAddress}
                    boxStyle={styles.addressBox}
                  />
                </Box>
                <Divider orientation="horizontal" />
                <Box sx={styles.notesBox}>
                  <Typography sx={{ color: "#0103077F", mb: 1 }}>
                    Notes:
                  </Typography>
                  <Typography sx={styles.baptismNotes}>{event.note}</Typography>
                </Box>
                <Divider orientation="horizontal" sx={{ mb: 2 }} />
                {isAdminOrManager && (
                  <Box
                    sx={{
                      display: "flex",
                      flex: 1,
                      pt: 2,
                      justifyContent: "center",
                    }}
                  >
                    <Chip
                      icon={
                        <SeeAttendeesIcon
                          color={"#00169E"}
                          weight={"light"}
                          size={!isLargeScreen ? 18 : 24}
                        />
                      }
                      label="View Attendees"
                      onClick={() => navigate(`/attendees/${churchId}`)}
                      sx={{ mr: 2, flex: 0.5 }}
                    />
                    <Chip
                      label="Edit"
                      icon={<EditIcon />}
                      onClick={() => {
                        navigate(`./baptism/${event.baptismId}/manage`, {
                          state: event,
                        });
                      }}
                      sx={{ flex: 0.5 }}
                    />
                  </Box>
                )}
                {(!isAuthenticated(authStatus) || isAttendee(attributes)) && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      pt: 1,
                      pb: 1,
                    }}
                  >
                    {event?.baptismId &&
                    userRegistrations.has(event.baptismId) ? (
                      <DebouncedButton
                        variant={"outlined"}
                        onClick={() => {
                          setUnregisterDialog(event);
                        }}
                        sx={[registerButton, buttonContainer]}
                        fullWidth
                      >
                        Unregister
                      </DebouncedButton>
                    ) : (
                      <RegisterButton
                        isDisabled={
                          (isAuthenticated(authStatus) &&
                            isManagerRole(attributes)) ||
                          userRegistrations.size > 0
                        }
                        infoText={
                          userRegistrations.size > 0
                            ? existingRegistration
                            : isAuthenticated(authStatus) &&
                                isManagerRole(attributes)
                              ? userRoleRestriction
                              : ""
                        }
                        handlePress={() => {
                          if (!isAuthenticated(authStatus)) {
                            navigate(
                              `/account/login?churchId=${encodeURIComponent(churchId ?? "")}`,
                            );
                            navigate(0); //Force page to reload
                            return;
                          }
                          if (isManagerRole(attributes)) {
                            setAlert({
                              showAlert: true,
                              alertMsg: userRoleRestriction,
                              alertType: "error",
                            });
                            return;
                          }
                          if (userRegistrations.size > 0) {
                            setAlert({
                              showAlert: true,
                              alertMsg: existingRegistration,
                              alertType: "error",
                            });
                            return;
                          }
                          setRegisterDialog(event);
                        }}
                      />
                    )}
                  </Box>
                )}
              </div>
            </Box>
          ))}
          <ConfirmDialog
            open={!!registerDialog}
            onClose={() => setRegisterDialog(undefined)}
            onConfirm={() => handleRegisterForBaptism(registerDialog!)}
            title={`Confirm Baptism Registration?`}
            content={`
              •  Church Name: **${churchInfo?.name ?? ""}**
              \n•  Time: **${registerDialog?.time ? humaneReadableTime(registerDialog?.time, registerDialog!!.timezone!!) : ""}**
              \n•  Address: **${registerDialog?.address ? formatAddress(registerDialog?.address) : ""}**`}
            buttonColor={"#00169E"}
          />
          <ConfirmDialog
            open={!!unregisterDialog}
            onClose={() => setUnregisterDialog(undefined)}
            onConfirm={() => handleUnregisterForBaptism(unregisterDialog!)}
            title={`Remove Baptism Registration?`}
            content={`Are you sure you want to unregister for the baptism event at: ${churchInfo?.name ?? ""}?`}
          />
        </Box>
      )
    );
  };

  const ChurchManagers = () => {
    const managerInfoText = `Add staff & leaders to your team who may want to access the backend.
      This information is private.`;
    return (
      churchManagers && (
        <Box sx={styles.paper}>
          <Box sx={styles.innerBox}>
            <Typography align={"left"} variant={"h6"}>
              Church Managers
            </Typography>
            <Button
              variant={"outlined"}
              startIcon={<AddBoxIcon />}
              onClick={() => setAddManagerDialog(true)}
            >
              New
            </Button>
          </Box>
          {isAdminOrManager && (
            <Box sx={styles.infoBox}>
              <Typography align={"left"} sx={styles.infoText}>
                <InfoIcon sx={styles.infoIcon} />
                {managerInfoText}
              </Typography>
            </Box>
          )}
          <Divider sx={{ mt: 1 }} orientation="horizontal" />
          {churchManagers.map((m) => (
            <div key={m.sub}>
              <Box sx={styles.manager}>
                <Typography align={"left"} sx={{ color: "#0103077F" }}>
                  Name:
                </Typography>
                <Box
                  alignSelf={"right"}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <Typography>{`${m.firstName} ${m.lastName}`}</Typography>
                  <IconButton onClick={() => setRemoveManager(m)}>
                    <DeleteIcon color="error" />
                  </IconButton>
                </Box>
              </Box>
              <Box sx={styles.manager}>
                <Typography align={"left"} sx={{ color: "#0103077F" }}>
                  Email:
                </Typography>
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                  align={"right"}
                >
                  {m.email}
                </Typography>
              </Box>
            </div>
          ))}
          <ConfirmDialog
            open={!!removeManager}
            onClose={() => setRemoveManager(undefined)}
            onConfirm={() =>
              handleRemoveManager(churchId!!, removeManager!!.sub!!)
            }
            title={`Delete ${removeManager?.firstName} ${removeManager?.lastName}?`}
            content={`Are you sure you want to remove ${removeManager?.firstName} ${removeManager?.lastName} as a manager? Their account will be deleted, this action cannot be undone.`}
          />
          <AddManagerDialog
            open={addManagerDialog}
            churchId={churchId!!}
            onClose={() => setAddManagerDialog(false)}
            onConfirm={(input: AddManagerToChurchInput) =>
              handleAddManager(input)
            }
          />
        </Box>
      )
    );
  };

  return (
    <Container maxWidth={isLargeScreen ? "md" : false}>
      <Box sx={styles.titleBox} maxWidth={theme.breakpoints.values.md}>
        <TopNavBar
          showBackArrow={true}
          handleNav={() => navigate("/churches", { replace: true })}
        />
      </Box>
      {churchInfo ? (
        <Typography
          align={"center"}
          variant={"h3"}
          fontFamily={"AmericanGrotesk-Bold"}
          sx={{ mb: 2 }}
        >
          {churchInfo.name}
        </Typography>
      ) : null}
      {isLoading ? (
        <Box sx={{ width: "100%", display: "block" }}>
          <LinearProgress />
        </Box>
      ) : (
        <>
          <BaptismSalvationReport />
          <ChurchDetails />
          <ChurchBaptisms />
          <ChurchManagers />
        </>
      )}
    </Container>
  );
};
export default ViewChurch;
